// src/pages/Newsletters/NewslettersPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  getAllNewsletters,
  deleteNewsletter,
} from "./../../services/newsletterServices";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import Button from "./../../components/Button/Button";
import ButtonEdit from "./../../components/Button/ButtonEdit";
import ButtonDelete from "./../../components/Button/ButtonDelete";
import CreateNewsletterModal from "./CreateNewsletterModal";
import EditNewsletterModal from "./EditNewsletterModal";
import { toast, ToastContainer } from "react-toastify";
import { FaPlus, FaFilter, FaSearch } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const moisOrder = [
  "JANVIER",
  "FÉVRIER",
  "MARS",
  "AVRIL",
  "MAI",
  "JUIN",
  "JUILLET",
  "AOÛT",
  "SEPTEMBRE",
  "OCTOBRE",
  "NOVEMBRE",
  "DÉCEMBRE",
];

const NewslettersPage = () => {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [newsletters, setNewsletters] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);

  const [selectedYear, setSelectedYear] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const canModifyNewsletter =
    userRole === "Administrateur" || userRole === "Bureau";

  // Chargement + tri (année desc, puis mois)
  const fetchNewsletters = async () => {
    try {
      const data = await getAllNewsletters();
      const sortedData = data.sort((a, b) => {
        if (b.annee_newsletter !== a.annee_newsletter) {
          return b.annee_newsletter - a.annee_newsletter;
        }
        return (
          moisOrder.indexOf(b.mois_newsletter.toUpperCase()) -
          moisOrder.indexOf(a.mois_newsletter.toUpperCase())
        );
      });
      setNewsletters(sortedData);
    } catch (error) {
      console.error("Erreur lors de la récupération des newsletters:", error);
      toast.error("Erreur lors du chargement des newsletters.");
    }
  };

  useEffect(() => {
    fetchNewsletters();
  }, []);

  // Liste d’années disponibles (pour le filtre)
  const availableYears = useMemo(() => {
    const yearsSet = new Set(
      newsletters.map((n) => Number(n.annee_newsletter)).filter(Boolean)
    );
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [newsletters]);

  // Filtrage par année + recherche (mois)
  const filteredNewsletters = useMemo(() => {
    let list = [...newsletters];

    if (selectedYear) {
      list = list.filter(
        (n) => Number(n.annee_newsletter) === Number(selectedYear)
      );
    }

    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      list = list.filter((n) =>
        `${n.mois_newsletter} ${n.annee_newsletter}`.toLowerCase().includes(t)
      );
    }

    return list;
  }, [newsletters, selectedYear, searchTerm]);

  // Suppression
  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Êtes-vous sûr de vouloir supprimer définitivement cette newsletter ?"
      )
    )
      return;

    try {
      await deleteNewsletter(id);
      await fetchNewsletters();
      toast.success("La newsletter a été supprimée avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de la newsletter:", error);
      toast.error("Erreur lors de la suppression de la newsletter.");
    }
  };

  const handleEdit = (newsletter) => {
    setSelectedNewsletter(newsletter);
    setShowEditModal(true);
  };

  return (
    <div className="min-h-screen flex flex-col w-full mx-auto pt-[130px] sm:pt-[130px] md:pt-[150px] lg:pt-[150px] xl:pt-[150px] pb-10 bg-slate-50 dark:bg-slate-900 dark:text-white">
      <ScrollIndicator />

      {/* En-tête */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader
          eyebrow="Visiteurs & Utilisateurs"
          title="Newsletters"
          subtitle="Retrouvez l’ensemble des newsletters du Comité, classées par mois et par année. Accédez facilement aux éditions les plus récentes ou replongez dans les saisons précédentes."
          align="center"
          size="lg"
        />

        <InfoBubble title="Bienvenue sur la page des Newsletters">
          <p className="text-sm">
            Consultez les lettres d’informations publiées par le Comité
            Départemental de Judo. Utilisez les filtres pour afficher une année
            précise ou rechercher un mois particulier.
          </p>
        </InfoBubble>
      </div>

      {/* Barre d’actions & filtres */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg">
          <div className="h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />
          <div className="p-4 md:p-5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            {/* Filtres à gauche */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="w-full sm:w-40">
                <label className="flex items-center gap-1 text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300 mb-1">
                  <FaFilter className="text-[10px]" />
                  Année
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="select select-bordered w-full dark:bg-slate-800 dark:border-slate-700 dark:text-white text-sm"
                >
                  <option value="">Toutes les années</option>
                  {availableYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-full sm:w-48">
                <label className="flex items-center gap-1 text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300 mb-1">
                  <FaSearch className="text-[10px]" />
                  Recherche
                </label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                  <input
                    type="text"
                    placeholder="Ex : mars, septembre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800/90 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/70"
                  />
                </div>
              </div>
            </div>

            {/* Actions à droite */}
            <div className="flex flex-wrap justify-start md:justify-end gap-2">
              {canModifyNewsletter && (
                <Button
                  text="Créer une Newsletter"
                  onClick={() => setShowCreateModal(true)}
                  className="gap-2 text-sm"
                  icon={FaPlus}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Liste des newsletters */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {filteredNewsletters.length === 0 ? (
          <div className="text-center text-sm text-slate-500 dark:text-slate-300 mt-8">
            Aucune newsletter ne correspond à vos critères pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {filteredNewsletters.map((newsletter) => (
              <article
                key={newsletter.id}
                className="relative group h-64 rounded-2xl overflow-hidden shadow-lg border border-slate-200/80 dark:border-slate-700/80 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col items-center justify-center"
              >
                {/* Filigrane logo (tu peux remplacer par logo_comite si tu veux) */}
                <div
                  className="absolute inset-0 opacity-15 group-hover:opacity-25 transition-opacity duration-300"
                  style={{
                    backgroundImage:
                      "url('https://res.cloudinary.com/dw2x1voyb/image/upload/v1721292835/logo_cd33_mbm9le.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />

                {/* Boutons admin */}
                {canModifyNewsletter && (
                  <div className="absolute top-3 right-3 flex space-x-2 z-10">
                    <ButtonDelete
                      onClick={() => handleDelete(newsletter.id)}
                      hoverLabel="Supprimer la newsletter"
                    />
                    <ButtonEdit
                      onClick={() => handleEdit(newsletter)}
                      hoverLabel="Éditer la newsletter"
                    />
                  </div>
                )}

                {/* Contenu texte */}
                <div className="relative z-10 flex flex-col items-center text-center px-4">
                  <span className="text-[11px] font-semibold tracking-[0.32em] uppercase text-primary-100/90 mb-2">
                    Comité Gironde
                  </span>
                  <h3 className="text-2xl font-montserrat font-extrabold tracking-[0.18em] uppercase mb-2">
                    Newsletter
                  </h3>
                  <p className="text-sm md:text-base italic px-3 py-1 rounded-full bg-black/40 backdrop-blur mb-4">
                    {newsletter.mois_newsletter} {newsletter.annee_newsletter}
                  </p>

                  <Button
                    text="Voir la Newsletter"
                    onClick={() =>
                      window.open(newsletter.lien_newsletter, "_blank")
                    }
                    className="relative gap-2 text-xs uppercase tracking-[0.16em] bg-primary/90 hover:bg-primary shadow-lg"
                    hoverLabel="Ouvrir dans un nouvel onglet"
                  />
                </div>

                {/* Halo hover */}
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-40 bg-radial from-primary/40 via-transparent to-transparent transition-opacity duration-500" />
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Modales */}
      {showCreateModal && (
        <CreateNewsletterModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          refreshList={fetchNewsletters}
        />
      )}
      {showEditModal && selectedNewsletter && (
        <EditNewsletterModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          refreshList={fetchNewsletters}
          newsletter={selectedNewsletter}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default NewslettersPage;
